import { useContext, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useQuery } from "@apollo/client"
import { Button, Chip } from "@heroui/react"
import { Divider, EventIconSummary, EventStatusBadge, LoadingPlaceholder, SimplePlaceholder, SearchInput } from "@components"
import { UserContext } from "@context"
import { CalendarDaysIcon, CircleStackIcon } from "@heroicons/react/24/outline"
import { GET_EVENTS_LIST } from "@graphql"
import { hasMeetingType, mySqlToLocal } from "@helpers"
import { truncate } from "lodash"

const EventList = () => {
  const { user } = useContext(UserContext)
  const [searchTerm, setSearchTerm] = useState("")
  const searchInputRef = useRef(null)

  const { loading, error, data } = useQuery(GET_EVENTS_LIST, {
    variables: {
      fetchPolicy: "network-and-cache",
      searchTerm,
      clientId: user.me.client.id,
      orderBy: [{ sessions: { aggregate: "MIN", column: "STARTS_AT" }, order: "DESC" }],
      page: 1,
      perPage: 50,
    },
  })

  const getUrl = (eventId) => {
    return user.clientUserRoles.organiser 
      ? `/event/${eventId}/dashboard`
      : `/event/${eventId}/schedule`
  }
  
  const handleClearSearch = () => {
    searchInputRef.current.value = ""
    setSearchTerm("")
  }

  const getEventStartDate = startDate => (startDate ? mySqlToLocal(startDate).format("DD/MM/YYYY") : "No Date Set")

  if (error) return "error"

  return (
    <div>
      <SearchInput
        innerRef={searchInputRef}
        classNames="mb-3"
        debounceMilliSeconds={300}
        placeholder="Search by title / description"
        handleChange={searchTerm => setSearchTerm(searchTerm)}
        handleClear={() => setSearchTerm("")}
      />

      {loading && <LoadingPlaceholder caption="Loading events..." icon={CalendarDaysIcon} />}
      
      {!loading && data.client.events.data.length === 0 && (
        <SimplePlaceholder icon={CircleStackIcon} title="No events found">
          <p className="mt-1 text-sm text-gray-500">
            You currently have no events.{" "}
            {searchTerm.length > 0 && (
              <span>
                Try a different search term.{" "}
                <Button className="mt-4" color="primary" fullWidth onPress={() => handleClearSearch()}>
                  Clear Search
                </Button>
              </span>
            )}
          </p>
        </SimplePlaceholder>
      )}

      {!loading && data.client.events.data.length > 0 && (
        <ul className="text-content2-foreground">
          {data.client.events.data.map(event => {
            const url = getUrl(event.id)
            
            return (
              <li key={event.id}>
                <Link to={url}>
                  <div className="flex items-center justify-between py-2">
                    <div className="mr-3">
                      <h1 className="font-semibold leading-6 text-foreground text-md">{event.title}</h1>

                      <p className="mt-1 mb-2 text-sm leading-5 text-content2-foreground">
                        {truncate(event.description, { length: 140 })}
                      </p>

                      <EventIconSummary
                        isVenue={hasMeetingType("VENUE", event.meetingTypes)}
                        isPhone={hasMeetingType("PHONE", event.meetingTypes)}
                        isVc={hasMeetingType("VC", event.meetingTypes)}
                        isVcTimer={event.vc_auto_disconnect}
                        isOpenToParents={event.open_parents}
                        isOpenToStaff={event.open_staff}
                        isParentBookingsDisabled={event.disable_parent_bookings}
                      />
                    </div>

                    <div className="flex flex-col">
                      <Chip className="mb-1" size="sm" color="primary">
                        <span className="flex items-center px-2 text-xs font-semibold">
                          <CalendarDaysIcon className="h-4 mr-2" />
                          {getEventStartDate(event.start_date)}
                        </span>
                      </Chip>

                      <EventStatusBadge size="sm" startDate={event.start_date} endDate={event.end_date} />
                    </div>
                  </div>
                </Link>

                <Divider className="my-1" />
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default EventList
